import { useState, useEffect, useCallback, useRef } from "react";
import { Inbox } from "@/components/inbox/Inbox";
import { PortalQuotes, PortalInvoices } from "@/components/billing/PortalBilling";
import { SideTabs } from "@/components/SideTabs";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Settings, CreditCard, MessageSquare, ShoppingCart, Video, Send, Plus, Trash2, FileText, Check, Download, Layers, DollarSign, Gift, Wrench } from "lucide-react";
import { ShopTab } from "@/components/shop/ShopTab";
import { GiftCardWallet } from "@/components/shop/GiftCardWallet";
import { MyPagesTab } from "@/components/MyPagesTab";
import { ZellePayPanel } from "@/components/zelle/ZellePay";
import { MyStatementsTab } from "@/components/billing/MyStatementsTab";
import { MyReceiptsTab } from "@/components/billing/MyReceiptsTab";
import { MyReportsTab } from "@/components/MyReportsTab";
import { MyRecordingsTab } from "@/components/recordings/MyRecordingsTab";
import { MyPhoneCard } from "@/components/MyPhoneCard";
import { CustomerMeetings } from "@/components/CustomerMeetings";
import { toast } from "sonner";
import api from "@/lib/api";
import { useInvoicePDF } from "@/lib/useInvoicePDF";
import { LogoUploader } from "@/components/LogoUploader";
import { ProductLogo } from "@/components/ProductLogo";
import { productById, fileUrl } from "@/lib/products";
import { MySeoLink } from "@/components/seo-admin/MySeoLink";

export default function CustomerDashboard() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const cartPollStarted = useRef(false);
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid || cartPollStarted.current) return;
    cartPollStarted.current = true;
    (async () => {
      for (let i = 0; i < 8; i++) {
        try {
          const { data } = await api.get(`/api/checkout/status/${sid}`);
          if (data.payment_status === "paid") { toast.success("Payment received — thank you!"); break; }
          if (data.status === "expired") { toast.error("Payment session expired"); break; }
        } catch { /* retry */ }
        await new Promise(r => setTimeout(r, 2000));
      }
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, setSearchParams]);
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || "");
  const saveLogo = async (url) => {
    await api.put("/api/auth/me/logo", { logo_url: url });
    setLogoUrl(url);
    checkAuth?.();
  };
  const [dashboard, setDashboard] = useState(null);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [messages, setMessages] = useState([]);
  const [unreadInbox, setUnreadInbox] = useState(0);
  useEffect(() => { const f = () => api.get("/api/inbox/unread").then(r => setUnreadInbox(r.data.unread)).catch(() => {}); f(); const t = setInterval(f, 20000); return () => clearInterval(t); }, []);
  const [msgInput, setMsgInput] = useState("");
  const [billing, setBilling] = useState({ transactions: [], subscription_plan: null });
  const [quotes, setQuotes] = useState([]);
  const [conferences, setConferences] = useState([]);
  const { generatePDF } = useInvoicePDF();

  const loadData = useCallback(async () => {
    try {
      const [d, c, m, b, q, conf] = await Promise.all([
        api.get("/api/customer/dashboard"),
        api.get("/api/shop/cart"), api.get("/api/messages"),
        api.get("/api/customer/billing"), api.get("/api/quotes"),
        api.get("/api/conferences"),
      ]);
      setDashboard(d.data); setCart(c.data);
      setMessages(m.data); setBilling(b.data); setQuotes(q.data);
      setConferences(conf.data);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const sendMsg = async () => {
    if (!msgInput.trim()) return;
    try { await api.post("/api/messages", { content: msgInput, to_user_id: "admin" }); setMsgInput(""); loadData(); }
    catch { toast.error("Failed to send"); }
  };
  const acceptQuote = async (quoteId) => {
    try { await api.put(`/api/quotes/${quoteId}/accept`); toast.success("Quote accepted"); loadData(); }
    catch { toast.error("Failed"); }
  };

  const plan = dashboard?.subscription_plan || billing?.subscription_plan;

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-12 px-4 md:px-8" data-testid="customer-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            {logoUrl && <img src={fileUrl(logoUrl)} alt="Shop logo" className="w-16 h-16 object-contain rounded-xl bg-white border border-slate-200 p-1" data-testid="customer-shop-logo" />}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.name}</h1>
              <p className="text-slate-500 text-sm">{user?.business_name || "Your Account"}</p>
            </div>
          </div>
          <LogoUploader logoUrl={logoUrl} onSave={saveLogo} testIdPrefix="customer-logo" label="Add your shop logo" />
        </div>

        {conferences.filter(c => c.status === "active").map(c => (
          <div key={c.id} className="bg-green-600 text-white rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3 shadow-sm" data-testid={`live-meeting-banner-${c.id}`}>
            <Video className="w-5 h-5 animate-pulse" /><p className="flex-1 font-semibold">“{c.title}” is live now with {c.host_name}</p>
            <Link to={`/conference/${c.id}`} className="bg-white text-green-700 font-bold px-4 py-2 rounded-lg text-sm" data-testid={`live-meeting-join-${c.id}`}>Join now</Link>
          </div>
        ))}

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Plan</p>
            <p className="text-xl font-bold text-blue-600">{plan?.name || "Free"}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Modules</p>
            <p className="text-xl font-bold text-green-600">{(dashboard?.enabled_modules || []).length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Cart Items</p>
            <p className="text-xl font-bold text-yellow-600">{cart.items.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Messages</p>
            <p className="text-xl font-bold text-purple-600">{unreadInbox}</p>
          </div>
        </div>

        <MySeoLink />
        <Link to="/shop/repair-orders" className="mb-4 flex items-center gap-3 bg-white border border-amber-200 hover:border-amber-500 rounded-xl p-4 transition-colors" data-testid="shop-ops-link"><Wrench className="w-5 h-5 text-amber-600" /><span className="flex-1"><b>Shop operations</b><span className="block text-sm text-slate-500">Repair orders, technician time clock, parts, The Stipulator and dealer inventory</span></span><span className="text-amber-600 font-bold">Open →</span></Link>
        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "software"} orientation="vertical" className="lg:flex lg:gap-6 lg:items-start">
          <SideTabs>
            <TabsTrigger value="software" className="text-xs sm:text-sm" data-testid="tab-software"><Settings className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Software</TabsTrigger>
            <TabsTrigger value="shop" className="text-xs sm:text-sm" data-testid="tab-shop"><ShoppingCart className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Shop & Cart</TabsTrigger>
            <TabsTrigger value="giftcards" className="text-xs sm:text-sm" data-testid="tab-giftcards"><Gift className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Gift Cards</TabsTrigger>
            <TabsTrigger value="billing" className="text-xs sm:text-sm" data-testid="tab-billing"><CreditCard className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Billing</TabsTrigger>
            <TabsTrigger value="quotes" className="text-xs sm:text-sm" data-testid="tab-quotes"><FileText className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Quotes</TabsTrigger>
            <TabsTrigger value="messages" className="text-xs sm:text-sm" data-testid="tab-messages"><MessageSquare className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Messages</TabsTrigger>
            <TabsTrigger value="meetings" className="text-xs sm:text-sm" data-testid="tab-meetings"><Video className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Conference Room</TabsTrigger>
            <TabsTrigger value="recordings" className="text-xs sm:text-sm" data-testid="tab-recordings"><Video className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Recordings</TabsTrigger>
            <TabsTrigger value="mypages" className="text-xs sm:text-sm" data-testid="tab-my-pages"><Layers className="w-3.5 h-3.5 mr-1 hidden sm:inline" />My Pages</TabsTrigger>
            <TabsTrigger value="reports" className="text-xs sm:text-sm" data-testid="tab-reports"><FileText className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Reports</TabsTrigger>
            <TabsTrigger value="statements" className="text-xs sm:text-sm" data-testid="tab-statements"><FileText className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Statements</TabsTrigger>
            <TabsTrigger value="zelle" className="text-xs sm:text-sm" data-testid="tab-zelle"><DollarSign className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Pay by Zelle</TabsTrigger>
            <TabsTrigger value="receipts" className="text-xs sm:text-sm" data-testid="tab-receipts"><Download className="w-3.5 h-3.5 mr-1 hidden sm:inline" />Receipts</TabsTrigger>
          </SideTabs>
          <div className="flex-1 min-w-0 space-y-4">

          <TabsContent value="mypages"><MyPagesTab /></TabsContent>
          <TabsContent value="reports"><MyReportsTab /></TabsContent>
          <TabsContent value="recordings"><MyRecordingsTab /></TabsContent>
          <TabsContent value="statements"><MyStatementsTab /></TabsContent>
          <TabsContent value="zelle"><div className="bg-white border border-slate-200 rounded-xl p-6 max-w-2xl"><h2 className="text-xl font-black text-violet-700 mb-4">Pay by Zelle — 1, 2, 3</h2><ZellePayPanel fromStatement /></div></TabsContent>
          <TabsContent value="receipts"><MyReceiptsTab /></TabsContent>

          {/* SOFTWARE / SUBSCRIPTION */}
          <TabsContent value="software">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Software & Subscription</h2>
              {plan ? (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{plan.name} Plan</h3>
                    <span className="text-2xl font-black text-blue-600">${plan.price}<span className="text-sm font-normal text-slate-500">/{plan.interval}</span></span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">{(plan.features || []).map((f, i) => <span key={i} className="text-xs bg-white text-blue-700 px-2.5 py-1 rounded-full border border-blue-200">{f}</span>)}</div>
                  <Link to="/pricing" className="inline-block mt-4 text-blue-600 text-sm font-medium hover:text-blue-500">Change Plan &rarr;</Link>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 text-center">
                  <p className="text-slate-500 mb-3">No active subscription</p>
                  <Link to="/pricing" className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg text-sm font-medium" data-testid="choose-plan-btn">Choose a Plan</Link>
                </div>
              )}
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Enabled Modules</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(dashboard?.enabled_modules || []).length > 0 ? (dashboard?.enabled_modules || []).map((mod, i) => {
                  const prod = productById(mod);
                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col items-center gap-2" data-testid={`enabled-module-${mod}`}>
                      {prod ? <ProductLogo product={prod} height={64} /> : <Check className="w-4 h-4 text-green-600" />}
                      <span className="text-xs font-medium text-slate-700 capitalize flex items-center gap-1"><Check className="w-3 h-3 text-green-600" />{prod?.name || mod.replace(/-/g, " ")}</span>
                    </div>
                  );
                }) : <p className="text-slate-400 text-sm col-span-3">No modules enabled. Contact admin or upgrade your plan.</p>}
              </div>
            </div>
          </TabsContent>

          {/* SHOP & GIFT CARDS */}
          <TabsContent value="shop"><ShopTab /></TabsContent>
          <TabsContent value="giftcards"><GiftCardWallet /></TabsContent>

          {/* BILLING */}
          <TabsContent value="billing">
            <PortalInvoices />
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Billing & Statements</h2>
              {billing.subscription_plan && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 flex items-center justify-between">
                  <div><p className="text-sm text-slate-500">Current Plan</p><p className="font-bold text-slate-900">{billing.subscription_plan.name}</p></div>
                  <p className="text-xl font-black text-blue-600">${billing.subscription_plan.price}/mo</p>
                </div>
              )}
              <Table>
                <TableHeader><TableRow className="border-slate-200"><TableHead className="text-slate-500">Date</TableHead><TableHead className="text-slate-500">Description</TableHead><TableHead className="text-slate-500">Amount</TableHead><TableHead className="text-slate-500">Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {billing.transactions.map(t => (
                    <TableRow key={t.id} className="border-slate-100">
                      <TableCell className="text-slate-600 text-sm">{t.created_at?.split("T")[0]}</TableCell>
                      <TableCell className="text-slate-900 text-sm">{t.plan_name || t.type || "Payment"}</TableCell>
                      <TableCell className="font-semibold text-slate-900">${t.amount}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-1 rounded-full ${t.payment_status === "paid" ? "bg-green-50 text-green-600" : "bg-yellow-50 text-yellow-600"}`}>{t.payment_status}</span></TableCell>
                    </TableRow>
                  ))}
                  {billing.transactions.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No transactions yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* QUOTES / INVOICES */}
          <TabsContent value="quotes">
            <PortalQuotes />
            {quotes.length > 0 && (<div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quotes & Invoices from Admin</h2>
              {quotes.length === 0 && <p className="text-slate-400 text-sm text-center py-8">No quotes yet</p>}
              <div className="space-y-4">
                {quotes.map(q => (
                  <div key={q.id} className={`border rounded-xl overflow-hidden ${q.status === "accepted" ? "border-green-300" : "border-slate-200"}`} data-testid={`quote-${q.id}`}>
                    <div className={`px-5 py-3 flex items-center justify-between ${q.status === "accepted" ? "bg-green-50" : "bg-slate-50"}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-900">Quote #{q.id?.slice(0, 8)}</span>
                        <span className="text-xs text-slate-400">{q.created_at?.split("T")[0]}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${q.status === "accepted" ? "bg-green-100 text-green-700" : q.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-500"}`}>{q.status}</span>
                    </div>
                    <div className="p-5">
                      {q.notes && <p className="text-slate-600 text-sm mb-4 italic bg-slate-50 p-3 rounded-lg">{q.notes}</p>}
                      <div className="space-y-2 mb-4">
                        {(q.items || []).map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${item.type === "subscription" ? "bg-blue-100 text-blue-700" : item.type === "addon" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{item.type === "subscription" ? "PLAN" : item.type === "addon" ? "ADD-ON" : "CUSTOM"}</span>
                              <span className="text-sm text-slate-700">{item.name}</span>
                            </div>
                            <span className="font-semibold text-sm text-slate-900">${item.price?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                        <span className="text-xl font-black text-slate-900">Total: ${q.total?.toFixed(2)}</span>
                        <div className="flex gap-2">
                          <button onClick={() => generatePDF(q.id)} className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5" data-testid={`pdf-quote-${q.id}`}><Download className="w-4 h-4" /> PDF</button>
                          {q.status === "pending" && <button onClick={() => acceptQuote(q.id)} className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-lg text-sm font-bold" data-testid={`accept-quote-${q.id}`}>Accept & Approve</button>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages"><Inbox /></TabsContent>

          {/* MEETINGS (view-only, can only join invited) */}
          <TabsContent value="meetings">
            <CustomerMeetings />
            <div className="mt-4"><MyPhoneCard /></div>
          </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
